import { FC, useMemo } from 'react';
import { List, ListItemButton, Typography, SxProps } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { navigation } from '@shared/routes';
import { NavLink } from '@cvt/components/NavLink';
import { useDictionary } from '@cvt/hooks/useDictionary';
import { useRouter } from '@cvt/routing';
import { Authenticated, NotAuthenticated } from '@modules/Auth/components';
import isUndefined from 'lodash/isUndefined';
import { useDialog } from '@cvt/contexts';


interface Props {

};

export const HeaderNav: React.FC<Props> = (props) => {
  const dictionary = useDictionary();
  const router = useRouter();

  const headerMenuRoutes = useMemo(() => {
    return navigation(router, dictionary).header || [];
  }, [dictionary, router]);

  const authIndependentRoutes = useMemo(() => {
    return headerMenuRoutes.filter(route =>  route.requiresAuth === undefined || route.requiresAuth === null);
  }, [headerMenuRoutes]);

  const authenticatedRoutes = useMemo(() => {
    return headerMenuRoutes.filter(route => !isUndefined(route.requiresAuth) && route.requiresAuth !== false);
  }, [headerMenuRoutes]);

  const notAuthenticatedRoutes = useMemo(() => {
    return headerMenuRoutes.filter(route => !isUndefined(route.requiresAuth) && route.requiresAuth === false);
  }, [headerMenuRoutes]);

  return (
    <List
      disablePadding sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'center',
        gap: { xs: 0, sm: 1 },
      }}
    >
      {authIndependentRoutes.map((item, idx) => <NavItem key={idx} item={item} />)}
      <Authenticated>
        {authenticatedRoutes.map((item, idx) => <NavItem key={idx} item={item} />)}
      </Authenticated>
      <NotAuthenticated>
        {notAuthenticatedRoutes.map((item, idx) => <NavItem key={idx} item={item} />)}
      </NotAuthenticated>
    </List>
  );
};

type NavItemProps = {
  item: CVT.Navigation.NavItem
}

const NavItem: FC<NavItemProps> = ({ item }) => {
  if (isExternalNavigation(item)) {
    return <ExternalItem item={item} />;
  }

  if (isDialogNavigation(item)) {
    return <DialogItem item={item} />;
  }

  return <NavigationItem item={item} />;
};

type NavigationItemProps = {
  item: CVT.Navigation.NavigationItem;
}

const NavigationItem: FC<NavigationItemProps> = ({ item }) => {
  const { icon, text, route, onClick, disabled } = item;

  return (
    <ListItemButton
      color="primary2"
      component={NavLink}
      to={route!}
      onClick={onClick}
      disabled={disabled}
      sx={listItemSx}
    >
      {icon}
      <Typography variant="body1">
        {text}
      </Typography>
    </ListItemButton>
  );
};

type ExternalItemProps = {
  item: CVT.Navigation.ExternalNavigationItem;
}

const ExternalItem: FC<ExternalItemProps> = ({ item }) => {
  return (
    <ListItemButton
      component="a"
      href={item.href}
      target="_blank"
      color="primary2"
      sx={listItemSx}
    >
      <Typography variant="body1" mr={0.5}>{item.text}</Typography>
      <OpenInNewIcon fontSize="inherit" />
    </ListItemButton>
  );
};

type DialogItemProps = {
  item: CVT.Navigation.DialogNavigationItem
}

const DialogItem: FC<DialogItemProps> = ({ item }) => {
  const { openDialog } = useDialog();

  const handleClick = () => openDialog(item.dialogId);

  return (
    <ListItemButton color="primary2" sx={listItemSx} onClick={handleClick}>
      <Typography variant="body1" mr={0.5}>{item.text}</Typography>
    </ListItemButton>
  );
};

const isExternalNavigation = (item: CVT.Navigation.NavItem): item is CVT.Navigation.ExternalNavigationItem => {
  return Boolean((item as CVT.Navigation.ExternalNavigationItem).href);
};

const isDialogNavigation = (item: CVT.Navigation.NavItem): item is CVT.Navigation.DialogNavigationItem => {
  return Boolean((item as CVT.Navigation.DialogNavigationItem).dialogId);
};

const listItemSx: SxProps = {
  px: { xs: 1, sm: undefined },
};
